import React, { ReactElement, useEffect, useState } from 'react';
import SiteHead from 'components/site-head';
import { useForm } from 'react-hook-form';
import { post } from 'lib/fetch';
import { useAuth } from 'lib/auth';
import { usePrevious } from 'lib/usePrevious';
import NavBarNoAuth from 'components/nav-bar-no-auth';
import { reportError } from 'lib/util';

Contact.requiresAuth = true;

export default function Contact(): ReactElement {
  const auth = useAuth();
  const authUser = auth?.state.user;
  const [status, setStatus] = useState<string>();
  const [error, setError] = useState<boolean>();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<{ email: string; leaveBlank: string; message: string }>();

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(location.search);

    if (urlSearchParams.has('message')) {
      setValue('message', urlSearchParams.get('message'));
    }
  }, [setValue]);

  const email =
    authUser &&
    (authUser.displayName && authUser.email
      ? `${authUser.displayName} <${authUser.email}>`
      : authUser.email);

  const previousEmail = usePrevious(email);

  useEffect(() => {
    if (!previousEmail && email) {
      setValue('email', email);
    }
  }, [previousEmail, email, setValue]);

  const onSubmit = async (data) => {
    setStatus('Your message is being sent...');

    try {
      await post(
        process.env.NEXT_PUBLIC_FUNCTIONS_BASE + 'submitContactForm',
        data
      );
      setStatus('Your message has been sent');
    } catch (e) {
      reportError(e);
      setError(true);
      setStatus('There was an error sending your message');
    }
  };

  return (
    <>
      <SiteHead title="Contact" description="TranscribeKit Contact Form" />
      <NavBarNoAuth />
      <header className="bg-white">
        <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900">Contact</h1>
        </div>
      </header>
      <main className="max-w-xl mx-auto px-4">
        {status ? (
          <p className={error && 'text-red-500'}>{status}</p>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="shadow sm:rounded-md sm:overflow-hidden">
              <div className="px-4 py-5 bg-white space-y-6 sm:p-6">
                <div className="col-span-6 sm:col-span-4">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email address
                  </label>
                  <input
                    type="text"
                    name="email"
                    id="email"
                    autoComplete="email"
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    {...register('email', { required: true })}
                  />
                  {errors.email && (
                    <span className="text-red-500">This field is required</span>
                  )}
                </div>
                <div className="hidden">
                  <label htmlFor="leaveBlank">Leave this blank</label>
                  <input
                    type="text"
                    name="leaveBlank"
                    id="leaveBlank"
                    {...register('leaveBlank')}
                  />
                </div>
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Your message
                  </label>
                  <div className="mt-1">
                    <textarea
                      id="message"
                      name="message"
                      rows={3}
                      className="shadow-sm focus:ring-blue-500 focus:border-blue-500 mt-1 block w-full sm:text-sm border-gray-300 rounded-md"
                      {...register('message', { required: true })}
                    />
                    {errors.message && (
                      <span className="text-red-500">
                        This field is required
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                <button
                  type="submit"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Send
                </button>
              </div>
            </div>
          </form>
        )}
      </main>
    </>
  );
}
